import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PicklistService } from "../services/administration/picklist/picklist.service";
import { FireDepartmentsBcTurnoversService } from "../services/firedepartments/fireDepartmentsBcTurnovers.service";
import { ActivatedRoute, Router } from "@angular/router";
import {
  IFireDepartmentBcTurnover,
  TurnoverCategory,
} from "../interfaces/firedepartment-bc-turnover/firedepartmentBcTurnover";
import { stat } from "fs";
import { MatTableDataSource } from "@angular/material/table";
import { ICarryoverComments } from "../interfaces/firedepartment-bc-turnover/firedepartmentCarryoverComments";
import { IPicklistValuesData } from "../interfaces/administration/picklistValuesData";
import { forkJoin, Subscription } from "rxjs";
import { LoggedUserService } from "../services/common/logged-user.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";
import { RoleNames } from "../utils/role-names";

@Component({
  selector: "app-ert-firedepartment-bc-turnover-edit",
  templateUrl: "./ert-firedepartment-bc-turnover-edit.component.html",
  styles: [
    `
      .button-container {
        display: flex !important;
        justify-content: space-between !important;
      }
    `,
  ],
})
export class ERTFiredepartmentBcTurnoverEditComponent
  implements OnInit, OnDestroy
{
  turnoverForm: FormGroup;
  bcTurnover: IFireDepartmentBcTurnover;
  categories: { id: string; text: string }[] = [
    { id: "0", text: "Firewater - Pump/Leak" },
    { id: "1", text: "Miscellaneous" },
    { id: "2", text: "Safety/Security" },
    { id: "3", text: "Equipment" },
    { id: "4", text: "Incident/Personnel" },
  ];
  subCategory: { id: string; text: string }[] = [
    { id: "0", text: "Pump" },
    { id: "1", text: "Leak" },
  ];
  showCarryoverComments: boolean = false;
  showEditCarryoverComments: boolean = false;
  carryoverEditComments: ICarryoverComments;
  recordId: number = 0;

  dataSource: MatTableDataSource<ICarryoverComments> =
    new MatTableDataSource<ICarryoverComments>();
  displayedColumns: string[] = [
    "id",
    "dateAdded",
    "createdBy",
    "crew",
    "shift",
  ];

  crewFieldId: number = 0;
  shiftFieldId: number = 0;
  categoryFieldId: number = 0;
  pumpFieldId: number = 0;
  isLoading: boolean = false;

  savedSuccessMessage: string = "";
  isDeleteEnabled: boolean = false;
  currentUser: ILoggedUserData;

  currentInformation: ICarryoverComments = {} as ICarryoverComments;

  constructor(
    private fb: FormBuilder,
    private pickListsService: PicklistService,
    private router: Router,
    private route: ActivatedRoute,
    private fireDepartmentsBcTurnoversService: FireDepartmentsBcTurnoversService,
    private loggedUserService: LoggedUserService
  ) {
    this.recordId = this.route.snapshot.params["id"];
    this.turnoverForm = this.fb.group({
      id: [this.recordId],
      status: ["", Validators.required],
      comments: [""],
      resolvedComments: [""],
    });
  }

  private subscriptions: Subscription[] = [];

  ngOnInit() {
    this.isLoading = true;

    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.currentUser = user;
      this.isDeleteEnabled =
        user.role === RoleNames.ERT_ADMINISTRATOR ? true : false;

      // Load all field IDs in parallel
      const fieldIdRequests = forkJoin({
        crew: this.pickListsService.getFieldIdByName("Crew"),
        shift: this.pickListsService.getFieldIdByName("Shift"),
        category: this.pickListsService.getFieldIdByName("Category"),
      });

      this.subscriptions.push(
        // Get field IDs
        fieldIdRequests.subscribe({
          next: (fields) => {
            this.crewFieldId = fields.crew || 0;
            this.shiftFieldId = fields.shift || 0;
            this.categoryFieldId = fields.category || 0;
          },
          error: (error) => {
            console.error("Error loading field IDs:", error);
            this.isLoading = false;
          },
        }),

        // Load turnover data
        this.fireDepartmentsBcTurnoversService
          .getFireDepartmentsBcTurnoversById(this.recordId)
          .subscribe({
            next: (data) => {
              if (!data) {
                this.isLoading = false;
                return;
              }

              this.bcTurnover = data;

              if (
                this.bcTurnover.facilityId !==
                Number(this.currentUser.facilityId)
              ) {
                this.router.navigate(["/no-access"]);
                return;
              }

              this.dataSource.data = data.carryoverComments.sort(
                (a, b) => b.id - a.id
              );

              this.setCurrentInformation(data.carryoverComments);
              this.turnoverForm.patchValue({
                status: data.status.toString(),
                comments: data.initialComments,
                resolvedComments: data.resolvedComments,
              });

              this.isLoading = false;
            },
            error: (error) => {
              console.error("Error loading turnover data:", error);
              this.isLoading = false;
            },
          })
      );
    });
  }

  ngOnDestroy() {
    // Clean up subscriptions to prevent memory leaks
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getCategoryName(turnoverCategory: TurnoverCategory): string {
    return TurnoverCategory[turnoverCategory];
  }

  cancel() {
    this.router.navigate(["/firedepartment-bc-turnover"]);
  }

  loadCarryoverComments() {
    this.isLoading = true;
    this.dataSource.data = [];
    this.fireDepartmentsBcTurnoversService
      .getCarryoverComments(this.recordId)
      .subscribe({
        next: (data) => {
          if (data) {
            console.log(data);
            this.dataSource.data = data;
            this.setCurrentInformation(data);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading carryover comments:", error);
          this.isLoading = false;
        },
      });
  }

  setCurrentInformation(rows: ICarryoverComments[]) {
    if (!rows || rows.length === 0) {
      // If there are no carryover comments, set current information from bcTurnover data
      if (this.bcTurnover) {
        this.currentInformation = {
          id: this.bcTurnover.id,
          dateAdded: this.bcTurnover.turnoverDate,
          comments: this.bcTurnover.initialComments,
          createdBy: this.currentInformation.createdBy,
          // Add required properties from ICarryoverComments interface
          turnoverId: this.recordId,
          crewId: this.bcTurnover.crewId,
          shiftId: this.bcTurnover.shiftId,
          picklistValues: this.bcTurnover.selectedValuesInTurnover || [],
          // Optional properties
          createdById: this.bcTurnover.createdById,
        };
      }
      return;
    }

    // If we have comments, continue with existing logic
    rows.sort((a, b) => {
      const dateComparison =
        new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return b.id - a.id;
    });
    this.currentInformation = rows[0];
  }

  carryoverComments() {
    this.showCarryoverComments = !this.showCarryoverComments;
  }

  handleSaved(data: any): void {
    this.loadCarryoverComments();
    this.carryoverComments();
  }

  handleEditSaved(data: any): void {
    this.loadCarryoverComments();
    this.showEditCarryoverComments = !this.showEditCarryoverComments;
  }

  handleEditCancelled(): void {
    this.carryoverEditComments = null;
    this.showEditCarryoverComments = !this.showEditCarryoverComments;
  }

  handleCancelled(): void {
    this.carryoverComments();
  }

  getLastSelectedValue(
    fieldType: "category" | "crew" | "shift" | "pump"
  ): string {
    if (!this.bcTurnover?.selectedValuesInTurnover) {
      return "";
    }

    let fieldId: number = 0;

    switch (fieldType) {
      case "category":
        fieldId = this.categoryFieldId;
        break;
      case "crew":
        fieldId = this.crewFieldId;
        break;
      case "shift":
        fieldId = this.shiftFieldId;
        break;
      case "pump":
        // You may need to add a pumpFieldId property to your component
        fieldId = this.pumpFieldId || 0;
        break;
    }

    // Find the matching item in the selectedValuesInTurnover array
    const item = this.bcTurnover.selectedValuesInTurnover.find(
      (val) => val.picklistFieldId === fieldId
    );

    return item ? item.value : "";
  }

  getPicklistValue(
    picklistValues: IPicklistValuesData[],
    fieldType: "category" | "crew" | "shift"
  ): string {
    let fieldId: number;

    switch (fieldType) {
      case "category":
        fieldId = this.categoryFieldId;
        break;
      case "crew":
        fieldId = this.crewFieldId;
        break;
      case "shift":
        fieldId = this.shiftFieldId;
        break;
    }

    return this.fireDepartmentsBcTurnoversService.getValueForField(
      picklistValues,
      fieldId
    );
  }

  confirmDeleteCarryoverComment(data: ICarryoverComments) {
    if (confirm("Are you sure you want to delete this record?")) {
      this.fireDepartmentsBcTurnoversService
        .deleteCarryoverComments(this.recordId, data.id)
        .subscribe((data) => {
          this.loadCarryoverComments();
        });
    }
  }

  editCarryoverComments(data: ICarryoverComments) {
    this.carryoverEditComments = data;
    this.showEditCarryoverComments = !this.showEditCarryoverComments;
  }

  submit() {
    if (this.turnoverForm.valid) {
      // Get the form values for the editable fields
      const initialComments = this.turnoverForm.get("comments")?.value;
      const resolvedComments = this.turnoverForm.get("resolvedComments")?.value;
      const status = this.turnoverForm.get("status")?.value;

      // Prepare update object - only include fields that should be updated
      const updateObj: Partial<IFireDepartmentBcTurnover> = {
        id: this.recordId,
        initialComments: initialComments,
        resolvedComments: resolvedComments || "",
        status: parseInt(status),
        resolvedDate: status === "1" ? new Date() : null,
      };

      // Call the service to update the record
      this.fireDepartmentsBcTurnoversService
        .updateBcTurnover(updateObj)
        .subscribe({
          next: (data) => {
            this.savedSuccessMessage = "Turnover updated successfully!";

            setTimeout(() => {
              this.savedSuccessMessage = "";
            }, 3000);

            window.scrollTo({ top: 0, behavior: "smooth" });
          },
          error: (error) => {
            // Error handling
            console.error("Error updating turnover:", error);
          },
        });
    } else {
      console.log("Form Invalid");
      this.turnoverForm.markAllAsTouched();
    }
  }
}
